import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment } from "react";
import useSWR from "swr";
import { Heading, Subheading } from "catalyst/heading";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from "catalyst/table";
import { LoadingBadge, LoadingErrorBadge } from "../components/badges.js";
import { mockApi, mockApiPrices, urlApiPrices, } from "../lib/api.js";
import { dateFormatter, getJson, mskTimeFormatter, rubFormatter, } from "../lib/util.js";
import { Text } from "catalyst/text";
const perMillionTokens = { per: 1_000_000, unit: "за 1 миллион токенов" };
// API prices are per million units; columns display prices per `per` units.
const usageTypeColumns = [
    { keys: ["input_tokens"], label: "Входящие", ...perMillionTokens },
    { keys: ["output_tokens"], label: "Исходящие", ...perMillionTokens },
    { keys: ["reasoning_tokens"], label: "Рассуждения", ...perMillionTokens },
    { keys: ["cache_read_tokens"], label: "Кэш, чтение", ...perMillionTokens },
    {
        keys: ["cache_write_tokens", "cache_5m_write_tokens"],
        label: "Кэш, запись",
        ...perMillionTokens,
    },
    {
        keys: ["cache_1h_write_tokens"],
        label: "Кэш, запись 1ч",
        ...perMillionTokens,
    },
    {
        keys: ["web_search_requests"],
        label: "Поиск",
        per: 1000,
        unit: "для поиска — за 1 тысячу запросов",
    },
    {
        keys: ["images"],
        label: "Изображение",
        per: 1,
        unit: "для изображений — за 1 изображение",
    },
    {
        keys: ["video_seconds"],
        label: "Видео, сек",
        per: 1,
        unit: "для видео — за 1 секунду",
    },
    {
        keys: ["input_characters"],
        label: "Текст",
        per: 1_000_000,
        unit: "для синтеза речи — за 1 миллион символов текста",
    },
    {
        keys: ["audio_seconds"],
        label: "Аудио, мин",
        per: 60,
        unit: "для распознавания речи — за 1 минуту аудио",
    },
];
/** First price defined among the column keys; the cache-write column merges
 * the generic and 5-minute writes, which no model declares together. */
const firstDefinedPrice = (prices, keys) => {
    for (const key of keys) {
        const value = prices[key];
        if (value !== undefined)
            return value;
    }
    return undefined;
};
const mediaSectionTitles = {
    images: "Генерация изображений",
    videos: "Генерация видео",
    speech: "Синтез речи",
    speech_recognition: "Распознавание речи",
    embeddings: "Эмбеддинги",
};
const hasPrice = (row, column) => column.keys.some((key) => row.model.prices[key] !== undefined);
const columnsFor = (rows) => usageTypeColumns.filter((column) => rows.some((row) => hasPrice(row, column)));
const lowestPrice = (row, column) => row.model.lowest_prices &&
    firstDefinedPrice(row.model.lowest_prices, column.keys);
const Muted = ({ children }) => (_jsx("span", { className: "text-zinc-500 dark:text-zinc-400", children: children }));
const PriceTable = ({ rows }) => {
    // A column with ranges splits into "от" and "до" cells so the words align.
    const columns = columnsFor(rows).map((column) => ({
        ...column,
        ranged: rows.some((row) => lowestPrice(row, column) !== undefined),
    }));
    return (_jsxs(Table, { className: "[&_table]:min-w-max", children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableHeader, { children: "\u041C\u043E\u0434\u0435\u043B\u044C" }), columns.map((column) => (_jsx(TableHeader, { colSpan: column.ranged ? 2 : 1, children: column.label }, column.label)))] }) }), _jsx(TableBody, { children: rows.map((row) => (_jsxs(TableRow, { children: [_jsx(TableCell, { children: row.modelLabel }), columns.flatMap((column) => {
                            const value = firstDefinedPrice(row.model.prices, column.keys);
                            const price = (amount) => (amount * column.per) / 1_000_000;
                            const label = value !== undefined && rubFormatter.format(price(value));
                            if (!column.ranged) {
                                return [
                                    _jsx(TableCell, { className: "text-right", children: label }, column.label),
                                ];
                            }
                            const lowest = lowestPrice(row, column);
                            const low = value !== undefined && lowest !== undefined && lowest < value
                                ? rubFormatter.format(price(lowest))
                                : undefined;
                            return [
                                _jsx(TableCell, { children: low === undefined ? (label) : (_jsxs(_Fragment, { children: [_jsx(Muted, { children: "\u043E\u0442" }), " ", low] })) }, `${column.label}:from`),
                                _jsx(TableCell, { children: low !== undefined && (_jsxs(_Fragment, { children: [_jsx(Muted, { children: "\u0434\u043E" }), " ", label] })) }, `${column.label}:to`),
                            ];
                        })] }, `${row.providerId}:${row.modelId}`))) })] }));
};
/** Fast-mode premium: OpenAI prices it as the `priority` service tier,
 * Anthropic as the `fast` speed. Everything else runs at the base rate. */
const fastMultiplier = (model) => model.service_tier?.["priority"] ?? model.speed?.["fast"];
const modifierColumns = [
    {
        key: "fast",
        label: "Fast режим",
        value: (model) => {
            const multiplier = fastMultiplier(model);
            return multiplier !== undefined
                ? `×${multiplierFormatter.format(multiplier)}`
                : undefined;
        },
    },
    {
        key: "long_context",
        label: "Длинный контекст",
        value: (model) => model.long_context && formatLongContext(model.long_context),
    },
    {
        key: "peak",
        label: "Пиковые часы",
        value: (model) => model.peak && formatPeak(model.peak),
    },
];
const multiplierFormatter = new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: 2,
});
const modelLabels = {};
const resolveModelLabel = (modelId, displayName) => modelLabels[modelId] ?? displayName ?? modelId;
const buildRows = (providerId, provider) => {
    const rows = [];
    const modelEntries = Object.entries(provider.models).sort(([a], [b]) => a.localeCompare(b));
    for (const [modelId, model] of modelEntries) {
        rows.push({
            providerId,
            modelId,
            modelLabel: resolveModelLabel(modelId, model.display_name),
            model,
        });
    }
    return rows;
};
const formatLongContext = (longContext) => `${multiplierFormatter.format(longContext.input_threshold_tokens / 1000)}k токенов: ` +
    `вход ×${multiplierFormatter.format(longContext.input_multiplier)}, ` +
    `выход ×${multiplierFormatter.format(longContext.output_multiplier)}`;
/** Render a UTC "HH:MM" clock time as MSK, anchored to an arbitrary UTC day. */
const formatMskTime = (time) => {
    const [hours = 0, minutes = 0] = time.split(":").map(Number);
    return mskTimeFormatter.format(new Date(Date.UTC(2026, 0, 1, hours, minutes)));
};
/** Peak windows arrive from the API as UTC ranges; the page shows them in MSK. */
const formatMskWindow = (window) => {
    const [start = "", end = ""] = window.split("-");
    return `${formatMskTime(start)}–${formatMskTime(end)}`;
};
const formatPeak = (peak) => `×${multiplierFormatter.format(peak.multiplier)} (${peak.windows
    .map(formatMskWindow)
    .join(", ")} МСК)`;
const Pricing = () => {
    const { data, error, isLoading } = useSWR(urlApiPrices, (url) => mockApi ? mockApiPrices : getJson(url));
    const providers = data
        ? Object.entries(data.providers).sort(([a], [b]) => a.localeCompare(b))
        : [];
    const rows = providers.flatMap(([providerId, provider]) => buildRows(providerId, provider));
    const textRows = rows.filter((row) => !mediaSectionTitles[row.model.category]);
    const sections = [
        {
            category: "text",
            title: "Использование AI моделей",
            rows: textRows,
        },
        ...Object.entries(mediaSectionTitles).map(([category, title]) => ({
            category,
            title,
            rows: rows.filter((row) => row.model.category === category),
        })),
    ].filter((section) => section.rows.length);
    const visibleUnits = [
        ...new Set(sections.flatMap((section) => columnsFor(section.rows).map(({ unit }) => unit))),
    ];
    const modifierRows = textRows.filter((row) => modifierColumns.some(({ value }) => value(row.model) !== undefined));
    const visibleModifierColumns = modifierColumns.filter(({ value }) => modifierRows.some((row) => value(row.model) !== undefined));
    const today = dateFormatter.format(new Date());
    return (_jsxs(_Fragment, { children: [_jsx(Heading, { className: "mb-6", children: "\u0422\u0430\u0440\u0438\u0444\u044B" }), isLoading && _jsx(LoadingBadge, {}), error && _jsx(LoadingErrorBadge, {}), !!sections.length && (_jsxs("div", { className: "grid grid-cols-1 gap-6", children: [sections.map((section) => (_jsxs(Fragment, { children: [_jsxs("div", { children: [_jsx(Subheading, { children: section.title }), (section.category === "images" ||
                                        section.category === "videos") && (_jsx(Text, { className: "mt-2", children: "\u0426\u0435\u043D\u044B \u043E\u0440\u0438\u0435\u043D\u0442\u0438\u0440\u043E\u0432\u043E\u0447\u043D\u044B\u0435. \u0418\u0442\u043E\u0433\u043E\u0432\u0430\u044F \u0441\u0442\u043E\u0438\u043C\u043E\u0441\u0442\u044C \u0437\u0430\u0432\u0438\u0441\u0438\u0442 \u043E\u0442 \u043F\u0430\u0440\u0430\u043C\u0435\u0442\u0440\u043E\u0432 \u0433\u0435\u043D\u0435\u0440\u0430\u0446\u0438\u0438." }))] }), _jsx(PriceTable, { rows: section.rows }), section.category === "text" && !!modifierRows.length && (_jsxs(_Fragment, { children: [_jsx(Subheading, { children: "\u041C\u043D\u043E\u0436\u0438\u0442\u0435\u043B\u0438 \u0441\u0442\u043E\u0438\u043C\u043E\u0441\u0442\u0438" }), _jsxs(Table, { className: "[&_table]:min-w-max", children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableHeader, { children: "\u041C\u043E\u0434\u0435\u043B\u044C" }), visibleModifierColumns.map((column) => (_jsx(TableHeader, { children: column.label }, column.key)))] }) }), _jsx(TableBody, { children: modifierRows.map((row) => (_jsxs(TableRow, { children: [_jsx(TableCell, { children: row.modelLabel }), visibleModifierColumns.map((column) => (_jsx(TableCell, { className: "whitespace-nowrap text-zinc-500 dark:text-zinc-400", children: column.value(row.model) }, column.key)))] }, `${row.providerId}:${row.modelId}`))) })] })] }))] }, section.title))), _jsxs("div", { className: "max-w-lg", children: [_jsxs(Text, { children: ["\u0422\u0430\u0440\u0438\u0444\u044B \u0443\u043A\u0430\u0437\u0430\u043D\u044B \u043F\u043E \u0441\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u044E \u043D\u0430 ", today, " \u0438 \u043E\u0431\u043D\u043E\u0432\u043B\u044F\u044E\u0442\u0441\u044F \u0435\u0436\u0435\u0434\u043D\u0435\u0432\u043D\u043E."] }), _jsxs(Text, { children: ["\u0421\u0442\u043E\u0438\u043C\u043E\u0441\u0442\u044C \u0443\u043A\u0430\u0437\u0430\u043D\u0430 ", visibleUnits.join(", "), "."] }), !!modifierRows.length && (_jsx(Text, { children: "\u041C\u043D\u043E\u0436\u0438\u0442\u0435\u043B\u0438 \u043F\u0440\u0438\u043C\u0435\u043D\u044F\u044E\u0442\u0441\u044F \u043A \u0431\u0430\u0437\u043E\u0432\u043E\u0439 \u0441\u0442\u0430\u0432\u043A\u0435: \u0441\u0442\u043E\u0438\u043C\u043E\u0441\u0442\u044C \u0437\u0430\u043F\u0440\u043E\u0441\u0430 \u0440\u0430\u0432\u043D\u0430 \u0431\u0430\u0437\u043E\u0432\u043E\u0439 \u0441\u0442\u0430\u0432\u043A\u0435, \u0443\u043C\u043D\u043E\u0436\u0435\u043D\u043D\u043E\u0439 \u043D\u0430 \u043C\u043D\u043E\u0436\u0438\u0442\u0435\u043B\u0438 Fast \u0440\u0435\u0436\u0438\u043C\u0430, \u0434\u043B\u0438\u043D\u043D\u043E\u0433\u043E \u043A\u043E\u043D\u0442\u0435\u043A\u0441\u0442\u0430 \u0438 \u043F\u0438\u043A\u043E\u0432\u044B\u0445 \u0447\u0430\u0441\u043E\u0432. \u0414\u043B\u044F \u043C\u043E\u0434\u0435\u043B\u0435\u0439, \u043A\u043E\u0442\u043E\u0440\u044B\u0445 \u043D\u0435\u0442 \u0432 \u0442\u0430\u0431\u043B\u0438\u0446\u0435 \u043C\u043D\u043E\u0436\u0438\u0442\u0435\u043B\u0435\u0439, \u0434\u0435\u0439\u0441\u0442\u0432\u0443\u0435\u0442 \u0442\u043E\u043B\u044C\u043A\u043E \u0431\u0430\u0437\u043E\u0432\u0430\u044F \u0441\u0442\u0430\u0432\u043A\u0430." }))] })] }))] }));
};
export default Pricing;
